import type { MaybeRefOrGetter } from 'vue';
import { ref, toValue, watchEffect } from 'vue';

export interface PinchState {
	event: Event;
	origin: PinchOrigin;
	scale: number;
	distance: number;
	startDistance: number;
}

export interface PinchOrigin {
	clientX: number;
	clientY: number;
}

export interface UsePinchOptions {
	onPinch: (state: PinchState) => void;
	onPinchStart?: (state: Omit<PinchState, 'scale'>) => void;
}

interface GestureEventLike extends Event {
	scale?: number;
}

// Pinch handlers need to cancel the browser's native zoom/scroll gesture before
// it reaches the page, so these listeners cannot be passive.
const addListenerOptions: AddEventListenerOptions = { passive: false };
const removeListenerOptions: EventListenerOptions = {};
const TOUCH_PINCH_SENSITIVITY = 2;
const WHEEL_PINCH_SENSITIVITY = 180;
const WHEEL_LINE_HEIGHT = 16;

/**
 * Normalizes pinch input across touch screens, macOS trackpad pinch gestures
 * exposed as ctrl/meta wheel events, and Safari's non-standard gesture events.
 */
export function usePinch(target: MaybeRefOrGetter<EventTarget | null | undefined>, options: UsePinchOptions) {
	const startDistance = ref<number | null>(null);

	watchEffect((onCleanup) => {
		const element = toValue(target);
		if (!element) return;

		element.addEventListener('touchstart', handleTouchStart as EventListener, addListenerOptions);
		element.addEventListener('touchmove', handleTouchMove as EventListener, addListenerOptions);
		element.addEventListener('touchend', handleTouchEnd as EventListener, addListenerOptions);
		element.addEventListener('touchcancel', resetPinch, addListenerOptions);
		element.addEventListener('wheel', handleWheel as EventListener, addListenerOptions);
		element.addEventListener('gesturestart', handleGestureStart as EventListener, addListenerOptions);
		element.addEventListener('gesturechange', handleGestureChange as EventListener, addListenerOptions);
		element.addEventListener('gestureend', resetPinch, addListenerOptions);

		onCleanup(() => {
			resetPinch();
			element.removeEventListener('touchstart', handleTouchStart as EventListener, removeListenerOptions);
			element.removeEventListener('touchmove', handleTouchMove as EventListener, removeListenerOptions);
			element.removeEventListener('touchend', handleTouchEnd as EventListener, removeListenerOptions);
			element.removeEventListener('touchcancel', resetPinch, removeListenerOptions);
			element.removeEventListener('wheel', handleWheel as EventListener, removeListenerOptions);
			element.removeEventListener('gesturestart', handleGestureStart as EventListener, removeListenerOptions);
			element.removeEventListener('gesturechange', handleGestureChange as EventListener, removeListenerOptions);
			element.removeEventListener('gestureend', resetPinch, removeListenerOptions);
		});
	});

	function handleTouchStart(event: TouchEvent) {
		if (event.touches.length !== 2) return;

		const distance = getTouchDistance(event.touches);
		if (distance === 0) return;
		const origin = getTouchOrigin(event.touches);

		startDistance.value = distance;
		options.onPinchStart?.({ event, origin, distance, startDistance: distance });
	}

	function handleTouchMove(event: TouchEvent) {
		if (event.touches.length !== 2) {
			resetPinch();
			return;
		}

		if (startDistance.value === null) return;

		if (event.cancelable) {
			event.preventDefault();
		}

		const distance = getTouchDistance(event.touches);
		const scale = (distance / startDistance.value) ** TOUCH_PINCH_SENSITIVITY;

		options.onPinch({
			event,
			origin: getTouchOrigin(event.touches),
			scale,
			distance,
			startDistance: startDistance.value,
		});
	}

	function handleWheel(event: WheelEvent) {
		if (!event.ctrlKey && !event.metaKey) return;

		if (event.cancelable) {
			event.preventDefault();
		}

		const scale = Math.min(Math.max(1 - getWheelDeltaY(event) / WHEEL_PINCH_SENSITIVITY, 0.5), 2);
		const origin = getPointerOrigin(event);

		options.onPinchStart?.({ event, origin, distance: 1, startDistance: 1 });
		options.onPinch({ event, origin, scale, distance: scale, startDistance: 1 });
	}

	function handleGestureStart(event: GestureEventLike) {
		if (event.cancelable) {
			event.preventDefault();
		}

		startDistance.value = 1;
		options.onPinchStart?.({ event, origin: getPointerOrigin(event), distance: 1, startDistance: 1 });
	}

	function handleGestureChange(event: GestureEventLike) {
		if (event.cancelable) {
			event.preventDefault();
		}

		const scale = (event.scale ?? 1) ** TOUCH_PINCH_SENSITIVITY;
		const origin = getPointerOrigin(event);

		if (startDistance.value === null) {
			startDistance.value = 1;
			options.onPinchStart?.({ event, origin, distance: 1, startDistance: 1 });
		}

		options.onPinch({ event, origin, scale, distance: scale, startDistance: 1 });
	}

	function handleTouchEnd(event: TouchEvent) {
		if (event.touches.length < 2) {
			resetPinch();
		}
	}

	function resetPinch() {
		startDistance.value = null;
	}

	return {
		resetPinch,
	};
}

function getTouchDistance(touches: TouchList) {
	const firstTouch = touches[0];
	const secondTouch = touches[1];

	if (!firstTouch || !secondTouch) return 0;

	return Math.hypot(
		secondTouch.clientX - firstTouch.clientX,
		secondTouch.clientY - firstTouch.clientY,
	);
}

function getTouchOrigin(touches: TouchList): PinchOrigin {
	const firstTouch = touches[0];
	const secondTouch = touches[1];

	if (!firstTouch || !secondTouch) return { clientX: 0, clientY: 0 };

	return {
		clientX: (firstTouch.clientX + secondTouch.clientX) / 2,
		clientY: (firstTouch.clientY + secondTouch.clientY) / 2,
	};
}

function getPointerOrigin(event: Event): PinchOrigin {
	return {
		clientX: 'clientX' in event && typeof event.clientX === 'number' ? event.clientX : 0,
		clientY: 'clientY' in event && typeof event.clientY === 'number' ? event.clientY : 0,
	};
}

function getWheelDeltaY(event: WheelEvent) {
	if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
		return event.deltaY * WHEEL_LINE_HEIGHT;
	}

	if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
		return event.deltaY * window.innerHeight;
	}

	return event.deltaY;
}
